import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Switch,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControlLabel,
  Grid,
  Pagination,
  Stack
} from "@mui/material";
import { collection, getDocs, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { Timestamp } from "firebase/firestore";
import { db, vertexAI } from "../firebaseConfig";
import PostulacionDetailsDialog from "./PostulacionDetailsDialog";
import SeguimientoPostulacionDialog from "./SeguimientoPostulacionDialog";
import { FaTrash } from "react-icons/fa";
import vertexAIServiceInstance from "../services/VertexAIService";

export default function PostulacionesList() {
  const [postulaciones, setPostulaciones] = useState([]);
  const [selectedPostulacion, setSelectedPostulacion] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [seguimientoDialogOpen, setSeguimientoDialogOpen] = useState(false);
  const [ofertaSeleccionada, setOfertaSeleccionada] = useState("todas");
  const [estadoSeleccionado, setEstadoSeleccionado] = useState("todos");
  const [ofertasDisponibles, setOfertasDisponibles] = useState([]);
  const [estadosDisponibles, setEstadosDisponibles] = useState([]);
  const [checkboxStates, setCheckboxStates] = useState({});
  const [respuestaVertexAI, setRespuestaVertexAI] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    const fetchPostulaciones = async () => {
      const querySnapshot = await getDocs(collection(db, "postulaciones"));
      const postulacionesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPostulaciones(postulacionesData);

      const ofertasUnicas = new Set();
      const estadosUnicos = new Set();
      postulacionesData.forEach((postulacion) => {
        ofertasUnicas.add(postulacion.ofertaTitulo);
        estadosUnicos.add(postulacion.estado);
      });
      setOfertasDisponibles(["todas", ...Array.from(ofertasUnicas)]);
      setEstadosDisponibles(["todos", ...Array.from(estadosUnicos)]);

      const initialCheckboxStates = {};
      postulacionesData.forEach((postulacion) => {
        initialCheckboxStates[postulacion.id] =
          postulacion.estado === "lista de espera";
      });
      setCheckboxStates(initialCheckboxStates);
    };

    fetchPostulaciones();
  }, []);

  const handleOpenDialog = (postulacion) => {
    setSelectedPostulacion(postulacion);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setSelectedPostulacion(null);
    setDialogOpen(false);
  };

  const formatFecha = (fecha) => {
    if (fecha instanceof Timestamp) {
      const date = fecha.toDate();
      return date.toLocaleDateString();
    }
    return "Fecha inválida";
  };

  const handleOpenSeguimientoDialog = (postulacion) => {
    setSelectedPostulacion(postulacion);
    setSeguimientoDialogOpen(true);
  };

  const handleCloseSeguimientoDialog = () => {
    setSelectedPostulacion(null);
    setSeguimientoDialogOpen(false);
  };

  const handleOfertaChange = (event) => {
    setOfertaSeleccionada(event.target.value);
    setCurrentPage(1);
  };

  const handleEstadoChange = (event) => {
    setEstadoSeleccionado(event.target.value);
    setCurrentPage(1);
  };

  const handleBusquedaChange = (event) => {
    setBusqueda(event.target.value.toLowerCase());
    setCurrentPage(1);
  };

  const handleEliminarPostulacion = async (id) => {
    if (window.confirm("¿Estás seguro de que quieres eliminar esta postulación?")) {
      try {
        const postulacionRef = doc(db, "postulaciones", id);
        await deleteDoc(postulacionRef);
        setPostulaciones(postulaciones.filter((postulacion) => postulacion.id !== id));
        console.log("Postulación eliminada con éxito");
      } catch (error) {
        console.error("Error al eliminar la postulación:", error);
      }
    }
  };

  const handleListaEsperaChange = async (event, postulacionId) => {
    const newCheckedState = event.target.checked;
    try {
      const postulacionRef = doc(db, "postulaciones", postulacionId);
      await updateDoc(postulacionRef, {
        estado: newCheckedState ? "lista de espera" : "pendiente",
      });

      setPostulaciones((prevPostulaciones) =>
        prevPostulaciones.map((postulacion) =>
          postulacion.id === postulacionId
            ? {
                ...postulacion,
                estado: newCheckedState ? "lista de espera" : "pendiente",
              }
            : postulacion
        )
      );

      setCheckboxStates((prevCheckboxStates) => ({
        ...prevCheckboxStates,
        [postulacionId]: newCheckedState,
      }));

      console.log("Estado de la postulación actualizado");
    } catch (error) {
      console.error("Error al actualizar el estado de la postulación:", error);
    }
  };

  const filteredPostulaciones = postulaciones.filter((postulacion) => {
    const coincideOferta =
      ofertaSeleccionada === "todas" ||
      postulacion.ofertaTitulo === ofertaSeleccionada;

    const coincideEstado =
      estadoSeleccionado === "todos" ||
      postulacion.estado === estadoSeleccionado;

    const coincideBusqueda =
      postulacion.informacionPersonal.nombresApellidos
        .toLowerCase()
        .includes(busqueda);

    return coincideOferta && coincideEstado && coincideBusqueda;
  });

  const uniquePostulaciones = [];
  const seenEmails = new Set();
  filteredPostulaciones.forEach((postulacion) => {
    const email = postulacion.informacionPersonal.correoElectronico;
    if (!seenEmails.has(email)) {
      seenEmails.add(email);
      uniquePostulaciones.push(postulacion);
    }
  });

  const totalPages = Math.ceil(uniquePostulaciones.length / pageSize);
  const paginatedPostulaciones = uniquePostulaciones.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const llamarVertexAI = async () => {
    try {
      const response = await vertexAIServiceInstance.enviarTexto("Hola, ¿cómo estás?");
      setRespuestaVertexAI(response);
    } catch (error) {
      console.error("Error al llamar a Vertex AI:", error);
    }
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Verificar Postulaciones
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id="oferta-select-label">Filtrar por Oferta</InputLabel>
            <Select
              labelId="oferta-select-label"
              id="oferta-select"
              value={ofertaSeleccionada}
              label="Filtrar por Oferta"
              onChange={handleOfertaChange}
            >
              {ofertasDisponibles.map((oferta) => (
                <MenuItem key={oferta} value={oferta}>
                  {oferta === "todas" ? "Todas las Ofertas" : oferta}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel htmlFor="buscador">Buscar por Nombre</InputLabel>
            <input
              id="buscador"
              type="text"
              value={busqueda}
              onChange={handleBusquedaChange}
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #ccc",
                borderRadius: "5px",
                fontSize: "16px",
              }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id="estado-select-label">Filtrar por Estado</InputLabel>
            <Select
              labelId="estado-select-label"
              id="estado-select"
              value={estadoSeleccionado}
              label="Filtrar por Estado"
              onChange={handleEstadoChange}
            >
              {estadosDisponibles.map((estado) => (
                <MenuItem key={estado} value={estado}>
                  {estado === "todos" ? "Todos los Estados" : estado}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table sx={{ width: "100%" }} aria-label="simple table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#1976d2" }}>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>N°</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Nombre y Apellidos</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Correo Electrónico</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Fecha de Postulación</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Oferta</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Estado</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedPostulaciones.map((postulacion, index) => (
              <TableRow
                key={postulacion.id}
                sx={{
                  backgroundColor: index % 2 === 0 ? "#ffffff" : "#f5f5f5",
                  "&:hover": {
                    backgroundColor: "#e0e0e0",
                  }
                }}
              >
                <TableCell>
                  {(currentPage - 1) * pageSize + index + 1}
                </TableCell>
                <TableCell component="th" scope="row">
                  {postulacion.informacionPersonal.nombresApellidos}
                </TableCell>
                <TableCell>
                  {postulacion.informacionPersonal.correoElectronico}
                </TableCell>
                <TableCell>
                  {formatFecha(postulacion.fechaPostulacion)}
                </TableCell>
                <TableCell>{postulacion.ofertaTitulo}</TableCell>
                <TableCell>{postulacion.estado}</TableCell>
                <TableCell>
  <Stack direction="row" spacing={1} alignItems="center">
    <Button
      size="small"
      variant="contained"
      color="primary"
      onClick={() => handleOpenDialog(postulacion)}
    >
      DETALLES
    </Button>
    <Button
      size="small"
      variant="contained"
      color="secondary"
      onClick={() => handleOpenSeguimientoDialog(postulacion)}
    >
      PROCESO
    </Button>
    <FormControlLabel
      control={
        <Switch
          checked={checkboxStates[postulacion.id] || false}
          onChange={(event) => handleListaEsperaChange(event, postulacion.id)}
          color="warning"
          size="small"
        />
      }
      labelPlacement="start"
      label="Espera"
      sx={{ ml: 1 }}
    />
    <Tooltip title="Eliminar">
      <IconButton
        onClick={() => handleEliminarPostulacion(postulacion.id)}
        color="error"
        aria-label="eliminar"
        size="small"
      >
        <FaTrash />
      </IconButton>
    </Tooltip>
  </Stack>
</TableCell>

              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Grid container justifyContent="center" sx={{ mt: 2, pb: 3 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
          />
        </Grid>
      )}

      {respuestaVertexAI && (
        <Typography variant="body1" sx={{ mt: 2 }}>
          Respuesta de Vertex AI: {respuestaVertexAI}
        </Typography>
      )}

      <PostulacionDetailsDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        postulacion={selectedPostulacion}
        formatFecha={formatFecha}
      />
      <SeguimientoPostulacionDialog
        open={seguimientoDialogOpen}
        onClose={handleCloseSeguimientoDialog}
        postulacion={selectedPostulacion}
      />
    </Container>
  );
}
